import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ZERO_QTY, qty, subQty, type LineSourceDef } from '@project/contracts'
import { Button, Icon, Input, Modal, Notice, Table, type ColumnDef } from '../design/index.js'
import { useApi, type LineRow, type PickLine, type PickResult } from '../app/api.js'
import { messageFor } from './feedback.js'
import { formatDate, formatQuantity, formatRate } from './format.js'

/**
 * The controls behind `def.lines.sources`.
 *
 * The estimate picker is the one that matters: a line picked from the BOQ
 * carries `fromLineId`, so the ledger knows what it was raised against and the
 * exception rules have something to measure. A line typed by hand does not,
 * and nothing downstream can reconstruct it.
 */

export interface LineSourcesProps {
  readonly docKey: string
  readonly documentId: string
  readonly sources: readonly LineSourceDef[]
  readonly lines: readonly LineRow[]
  readonly onAdd: (lines: readonly LineRow[]) => void
  /**
   * The grid the picked rows land on, when it is not the primary one — the
   * charges a purchase invoice selects from its orders (BILL-011).
   */
  readonly section?: string
  /**
   * The document is locked (an approved project): only Export is offered —
   * the BOQ can still be taken out to Excel without revising it.
   */
  readonly exportOnly?: boolean
  /** Unsaved edits on screen, which an import would leave behind. */
  readonly dirty?: boolean
}

/**
 * The sources the reference shows as tabs of one "Select items" picker —
 * Estimated Items and Requested Items (PUR-017). Each keeps what was ticked
 * on it while the other is looked at, and Add takes from all of them.
 */
const TABBED: Readonly<Record<string, string>> = {
  estimation: 'Estimated items',
  request: 'Requested items',
  requisition: 'Requisitioned items',
}

export const LineSources = ({ docKey, documentId, sources, lines, onAdd, section, exportOnly = false, dirty = false }: LineSourcesProps) => {
  const api = useApi()
  const picker = useRef<HTMLInputElement>(null)
  // One result per source looked at; the tab on screen is `active` (PUR-017).
  const [results, setResults] = useState<Record<string, PickResult>>({})
  const [active, setActive] = useState<string | null>(null)
  const pick = active ? results[active] ?? null : null
  const setPick = (next: null): void => { setActive(next); setResults({}) }
  const [chosen, setChosen] = useState<Record<string, string>>({})
  /*
    Reference: a row is taken only when its box is ticked; ticking proposes
    the open balance, which stays editable. Typing a quantity ticks the row.
  */
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set())
  const [hideBoqSections, setHideBoqSections] = useState(false)
  const [matchAt, setMatchAt] = useState(0)
  const [imported, setImported] = useState<Awaited<ReturnType<typeof api.importLines>> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [onlyChosen, setOnlyChosen] = useState(false)
  const [hideFullyRequested, setHideFullyRequested] = useState(false)
  const [view, setView] = useState<'ITEM' | 'BOQ'>('ITEM')
  const [warnUnsaved, setWarnUnsaved] = useState(false)

  const offered = sources.filter((s) => s.source !== 'order')
  const tabs = offered.filter((s) => TABBED[s.source] !== undefined)
  const tabbed = tabs.length >= 2
  if (offered.length === 0) return null

  /*
    Reference: Import BOQ first asks "Unsaved BOQ Changes? … If you proceed,
    the unsaved changes will be ignored." The file is read against what is
    saved, so edits still on screen are what would be lost.
  */
  const chooseFile = (): void => {
    if (dirty) setWarnUnsaved(true)
    else picker.current?.click()
  }

  if (exportOnly) {
    if (!offered.some((s) => s.source === 'excel')) return null
    return (
      <div className="line-sources">
        <a className="btn btn--ghost" href={api.importTemplateUrl(docKey, documentId, section)} download>
          <Icon name="dl" size={18} />
          <span>{docKey === 'project' ? 'Export BOQ' : 'Export to Excel'}</span>
        </a>
      </div>
    )
  }

  const load = useMutation({
    mutationFn: ({ source }: { source: string; opening: boolean }) => api.pickLines(docKey, documentId, source),
    onSuccess: (result, { source, opening }) => {
      setError(null)
      // Everything already on the document is off the list: picking the same
      // BOQ line twice is how a quantity gets ordered twice.
      // A request line answered on a transfer is kept as `mrLineId`.
      const already = new Set(lines.flatMap((l) => [String(l['fromLineId'] ?? ''), String(l['mrLineId'] ?? '')]))
      const fresh = result.lines.filter((l) => !l.fromLineId || !already.has(l.fromLineId))
      setResults((was) => ({ ...(opening ? {} : was), [source]: { ...result, lines: fresh } }))
      setActive(source)
      // Nothing is TAKEN until someone ticks it (docs/video-notes/06). From
      // the estimate each box does start at what is still open — the
      // reference proposes the balance — and a fully requested line starts
      // blank. Another tab keeps what was ticked on this one.
      // So does "Select items" over an order: what is still open on it, or
      // what the receipt named took in (INV-012). A stock adjustment's Remove
      // starts at each lot's balance too (STK-013).
      const proposed = result.source === 'estimation' || result.source === 'order'
        || (docKey === 'stock_adjustment' && result.source === 'stock')
        ? Object.fromEntries(fresh
          .filter((l) => l.openQty !== 'RO' && Number(l.openQty) > 0)
          .map((l) => [keyOf(l), l.openQty]))
        : {}
      if (!opening) {
        setChosen((was) => ({ ...proposed, ...was }))
        return
      }
      setTicked(new Set())
      setChosen(proposed)
      setHideBoqSections(false)
      setMatchAt(0)
      setSearch('')
      setOnlyChosen(false)
      setHideFullyRequested(false)
      setView('ITEM')
    },
    onError: (err) => setError(messageFor(err)),
  })

  /** Every line on every tab looked at, for what is taken and counted. */
  const everyLine = Object.values(results).flatMap((r) => r.lines)

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const base64 = await toBase64(file)
      return api.importLines(docKey, documentId, base64, section)
    },
    onSuccess: (result) => {
      setError(null)
      setImported(result)
    },
    onError: (err) => setError(messageFor(err)),
  })

  /**
   * Fill in what each line is ready to be billed for.
   *
   * A line with nothing done is left blank rather than set to zero: blank
   * means "not on this bill", and zero would put an empty line on the
   * customer's copy.
   */
  const autoFill = (): void => {
    if (!pick) return
    const next: Record<string, string> = { ...chosen }
    for (const line of pick.lines) {
      if (line.calculatedQty === undefined) continue
      if (Number(line.calculatedQty) <= 0) continue
      next[keyOf(line)] = line.calculatedQty
    }
    setChosen(next)
    setTicked(new Set(Object.keys(next).filter((k) => Number(next[k]) > 0)))
  }

  /**
   * The rare case where everything open really is wanted: an explicit choice,
   * over the lines on screen (so a search narrows it), never the default.
   * A rate-only line has no quantity to fill.
   */
  const fillOpen = (): void => {
    const next: Record<string, string> = { ...chosen }
    const tick = new Set(ticked)
    for (const line of shown) {
      if (line.openQty === 'RO' || !(Number(line.openQty) > 0)) continue
      next[keyOf(line)] = line.openQty
      tick.add(keyOf(line))
    }
    setChosen(next)
    setTicked(tick)
  }

  const takePicked = (): void => {
    if (!pick) return
    const next: LineRow[] = []
    let seq = lines.length
    const seen = new Set<string>()
    // Which picker each line came from: a transfer's quantity sits under From
    // reserved or From stock by it until the save works it out from the lot (STK-005).
    const sourceOf = new Map(Object.values(results).flatMap((r) => r.lines.map((l) => [keyOf(l), r.source] as const)))
    for (const line of everyLine) {
      if (seen.has(keyOf(line))) continue
      seen.add(keyOf(line))
      if (!ticked.has(keyOf(line))) continue
      const quantity = chosen[keyOf(line)] ?? ''
      if (quantity === '' || Number(quantity) <= 0) continue
      seq += 1
      next.push({
        seq,
        ...(section ? { section } : {}),
        itemId: line.itemId,
        itemLabel: line.itemLabel,
        description: line.description,
        unit: line.unit,
        quantity,
        rate: line.rate ?? '',
        // Set on the estimate and carried by the picker, so the person keying
        // the receipt never has to know which of 500 support items is billable.
        ...(line.isBillable === undefined ? {} : { isBillable: line.isBillable ? 'YES' : 'NO' }),
        ...(line.boqCode ? { boqRef: line.boqCode } : {}),
        // The term a line is billed under, which the picker has always
        // returned and this has always dropped — so the 'Billing term' column
        // was blank on every line the picker added, and the term had to be
        // read off the project instead of off the bill.
        ...(line.billingTermLineId ? { billingTermLineId: line.billingTermLineId } : {}),
        ...(line.hsnSacCode ? { hsnSacCode: line.hsnSacCode } : {}),
        ...(line.brand ? { brand: line.brand } : {}),
        ...(line.fromLineId ? { fromLineId: line.fromLineId } : {}),
        ...(docKey === 'material_transfer' ? {
          fromReserved: sourceOf.get(keyOf(line)) === 'reserved' ? quantity : '0',
          fromStock: sourceOf.get(keyOf(line)) === 'reserved' ? '0' : quantity,
          ...(line.fromLineId && line.documentNo ? { lotRef: [line.documentNo, line.receivedDate].filter(Boolean).join(' · ') } : {}),
        } : {}),
        // Whatever else the picker says the row starts with (docs/24).
        ...(line.carry ?? {}),
      })
    }
    if (next.length > 0) onAdd([...lines, ...next])
    setPick(null)
  }

  const takeImported = (): void => {
    if (!imported) return
    if (imported.accepted.length > 0) onAdd(mergeImported(lines, imported.accepted, imported.matchBy, section))
    setImported(null)
  }

  /** What has a quantity against it right now — the footer's own count. */
  const isTaken = (l: PickLine): boolean => ticked.has(keyOf(l)) && Number(chosen[keyOf(l)] ?? '0') > 0
  const takingCount = pick ? new Set(everyLine.filter(isTaken).map(keyOf)).size : 0
  const offeredCount = new Set(everyLine.map(keyOf)).size

  const query = search.trim().toLowerCase()
  const textOf = (l: PickLine): string =>
    `${l.itemLabel} ${l.description ?? ''} ${l.note ?? ''} ${l.brand ?? ''} ${l.documentNo ?? ''} ${l.requestNo ?? ''} ${l.boqCode ?? ''} ${l.boqDescription ?? ''}`
      .toLowerCase()
  // In the BOQ view a search finds rather than filters (reference: matches
  // bolded in place, "1 / 116" with ▲ ▼), so the BOQ around them stays put.
  const findInPlace = view === 'BOQ' && pick !== null && pick.source === 'estimation'

  const shown = pick
    ? pick.lines.filter((l) => {
      if (onlyChosen && !isTaken(l)) return false
      if (hideFullyRequested && Number(l.openQty) <= 0) return false
      if (query === '' || findInPlace) return true
      return textOf(l).includes(query)
    })
    : []

  const matches = findInPlace && query !== '' ? shown.filter((l) => textOf(l).includes(query)) : []
  const current = matches.length === 0 ? null : matches[Math.min(matchAt, matches.length - 1)]!
  const currentKey = current ? keyOf(current) : null
  useEffect(() => {
    if (!currentKey) return
    document.getElementById(`pick-${currentKey}`)?.scrollIntoView?.({ block: 'center' })
  }, [currentKey])
  const highlight = (text: string): ReactNode => {
    if (!findInPlace || query === '') return text
    const at = text.toLowerCase().indexOf(query)
    if (at < 0) return text
    return <>{text.slice(0, at)}<mark><strong>{text.slice(at, at + query.length)}</strong></mark>{text.slice(at + query.length)}</>
  }

  // Stock has nothing estimated about it; the estimate and the BOQ do.
  const estimated = pick !== null && !['stock', 'reserved', 'outstanding', 'charges', 'order'].includes(pick.source)
  /** Lots: their brand, receipt and arrival date are columns of their own. */
  const lotted = (pick?.lines ?? []).some((l) => l.receivedDate !== undefined && l.documentNo !== undefined)
  /** Lines that belong to a material request say which. */
  const requested = (pick?.lines ?? []).some((l) => l.requestNo)

  /**
   * The same lines, nested under the BOQ line they were estimated against.
   *
   * Built from what is already showing, so the search and both checkboxes
   * apply in either view; a group that nothing matches keeps its place in the
   * count but is not drawn, which is what the `n of m` reads against.
   */
  const groups = (() => {
    const byKey = new Map<string, {
      key: string
      ref: string | null
      narrative: string
      tendered: boolean | undefined
      lines: PickLine[]
    }>()
    for (const line of pick?.lines ?? []) {
      const key = line.boqLineId ?? '—'
      const existing = byKey.get(key)
      if (existing) continue
      byKey.set(key, {
        key,
        ref: line.boqCode ?? null,
        narrative: line.boqDescription ?? 'Not against any one BOQ line',
        tendered: line.tendered,
        lines: [],
      })
    }
    for (const line of shown) {
      byKey.get(line.boqLineId ?? '—')?.lines.push(line)
    }
    return [...byKey.values()]
  })()

  const matched = groups.filter((g) => g.lines.length > 0).length

  /**
   * Reference BOQ View: the whole BOQ in order — sections, narrative, sub-
   * headings — with the estimate rows under the line they cost, and the
   * common items (under no BOQ line) last. Without an outline (another
   * picker) it is the groups as they come.
   */
  const boqTree = (() => {
    const outline = pick?.boqOutline
    if (!outline) return groups.filter((g) => g.lines.length > 0).map((g) => ({ ...g, lineType: 'ITEM' }))
    const byBoq = new Map(groups.map((g) => [g.key, g.lines]))
    const nodes = outline.map((o) => ({
      key: o.id,
      ref: o.boqCode,
      narrative: o.description ?? '',
      tendered: o.tendered,
      lineType: o.lineType,
      lines: byBoq.get(o.id) ?? [],
    }))
    const common = byBoq.get('—') ?? []
    return common.length > 0
      ? [...nodes, { key: '—', ref: null, narrative: 'Common items and overheads', tendered: true, lineType: 'SECTION', lines: common }]
      : nodes
  })()
  const boqOrdered = boqTree.flatMap((n) => n.lines)

  /** Only the BOQ picker knows how much of a line is ready to be billed. */
  const calculable = (pick?.lines ?? []).some((l) => l.calculatedQty !== undefined)
  /** Only the buying documents are told what is already on the shelf. */
  const stocked = (pick?.lines ?? []).some((l) => l.stockOnHand !== undefined)


  const boqColumns: ColumnDef<PickLine>[] = estimated
    ? [
      { key: 'boqCode', header: 'Ref no', width: 110, render: (row) => row.boqCode ?? '—' },
      /*
        Tendered or not (docs/05 §5b). `NT` is extra work outside the original
        tender: it is picked the same way, but it is what Regularise BOQ later
        has to account for, so the person picking should be able to see which
        it is before they do.
      */
      { key: 'tendered', header: 'BOQ', width: 70,
        render: (row) => (row.tendered === false ? 'NT' : 'T') },
    ]
    : []

  const lotColumns: ColumnDef<PickLine>[] = lotted
    ? [
      { key: 'brand', header: 'Brand', width: 120, render: (row) => row.brand ?? '—' },
      { key: 'documentNo', header: 'Document no', width: 170,
        render: (row) => <span className="mono">{row.documentNo ?? '—'}</span> },
      { key: 'receivedDate', header: pick?.dateLabel ?? 'Received date', width: 130,
        render: (row) => (row.receivedDate ? formatDate(row.receivedDate) : '—') },
    ]
    : []

  const tickColumn: ColumnDef<PickLine> = {
    key: 'tick', header: '', width: 52,
    render: (row) => (
      <input
        type="checkbox"
        aria-label={`Take ${row.itemLabel}`}
        checked={ticked.has(keyOf(row))}
        onChange={(e) => {
          const on = e.currentTarget.checked
          setTicked((was) => {
            const next = new Set(was)
            if (on) next.add(keyOf(row)); else next.delete(keyOf(row))
            return next
          })
          // Ticked, it proposes the open balance; unticked, it clears.
          setChosen((was) => ({
            ...was,
            [keyOf(row)]: on
              ? (was[keyOf(row)] || (row.openQty !== 'RO' && Number(row.openQty) > 0 ? row.openQty : ''))
              : '',
          }))
        }}
      />
    ),
  }

  /** The estimate's allowed brands, and the job each line is for (PUR-018). */
  const branded = (pick?.lines ?? []).some((l) => l.allowedBrands)
  const multiProject = (pick?.lines ?? []).some((l) => l.projectName)

  const columns: ColumnDef<PickLine>[] = [
    tickColumn,
    ...boqColumns,
    ...(requested
      ? [{ key: 'requestNo', header: 'Material request', width: 150,
        render: (row: PickLine) => <span className="mono">{row.requestNo ?? '—'}</span> }]
      : []),
    { key: 'itemLabel', header: 'Material', width: 380,
      render: (row) => (
        <div
          id={`pick-${keyOf(row)}`}
          data-current={currentKey === keyOf(row) ? 'true' : undefined}
          style={currentKey === keyOf(row) ? { outline: '2px solid var(--accent)', outlineOffset: 2 } : undefined}
        >
          <div>{highlight(row.itemLabel)}</div>
          {row.description ? <div className="cell-sub">{highlight(row.description)}</div> : null}
          {/* A lot's facts have columns of their own; only where it sits stays here. */}
          {lotted
            ? (row.location ? <div className="cell-sub">{row.location}</div> : null)
            : (row.note && !(estimated && view === 'ITEM') ? <div className="cell-sub">{row.note}</div> : null)}
        </div>
      ) },
    /*
      Reference Item View: the parent BOQ line's own text, in a column of its
      own beside the material, rather than squeezed into a note.
    */
    ...(estimated && view === 'ITEM' && pick?.source === 'estimation'
      ? [{ key: 'boqDescription', header: 'BOQ', width: 260,
        render: (row: PickLine) => (row.boqDescription
          ? <span className="cell-sub" title={row.boqDescription}>{row.boqDescription.slice(0, 120)}{row.boqDescription.length > 120 ? '…' : ''}</span>
          : '—') }]
      : []),
    ...lotColumns,
    ...(branded
      ? [{ key: 'allowedBrands', header: 'Allowed brand', width: 150,
        render: (row: PickLine) => row.allowedBrands ?? '—' }]
      : []),
    ...(multiProject
      ? [{ key: 'projectName', header: 'Project', width: 180,
        render: (row: PickLine) => row.projectName ?? '—' }]
      : []),
    { key: 'unit', header: 'Unit', width: 90 },
    // A lot reads as the reference words it: Received, Used (STK-013).
    { key: 'availableQty', header: estimated ? 'Estimated' : lotted ? 'Received' : 'In total', width: 120, align: 'right',
      render: (row) => <span className="num">{formatQuantity(row.availableQty)}</span> },
    /*
      What has already been raised against this line, from wherever (docs/15
      R-4.3). Without it, "still open" is a number with nothing to read it
      against: 40 left of 40 and 40 left of 400 are the same figure and very
      different situations.
    */
    { key: 'requested', header: estimated ? 'So far requested' : lotted ? 'Used' : 'Already taken', width: 150, align: 'right',
      render: (row) => (
        <span className="num">
          {/* A rate-only line has no quantity to have used up. */}
          {row.availableQty === 'RO' ? '—' : (
            <>
              {/* Reference: a dot on a line already fully (or over) requested. */}
              {Number(requestedSoFar(row)) > 0 && Number(row.openQty) <= 0 ? (
                <span
                  aria-label={overRequested(row) ? 'Over-requested' : 'Fully requested'}
                  title={overRequested(row) ? 'Over-requested' : 'Fully requested'}
                  style={{ color: overRequested(row) ? 'var(--bad)' : 'var(--muted)', marginRight: 4 }}
                >•</span>
              ) : null}
              {formatQuantity(requestedSoFar(row))}
            </>
          )}
        </span>
      ) },
    { key: 'openQty', header: 'Still open', width: 120, align: 'right',
      render: (row) => <span className="num">{formatQuantity(row.openQty)}</span> },
    /*
      What is already on the shelf. Asked here rather than in a report because
      it is asked WITH the line in front of you: "can check stock in warehouse"
      is the step that stops a site ordering what it already has
      (docs/video-notes/06 §9).
    */
    ...(stocked
      ? [{ key: 'stockOnHand', header: 'In stock', width: 116, align: 'right' as const,
        render: (row: PickLine) => (
          <span className="num">
            {row.stockOnHand === undefined ? '—' : formatQuantity(row.stockOnHand)}
          </span>
        ) }]
      : []),
    /*
      How much of this line has actually been done — goods received for supply,
      work progress for installation, billable estimates only. It is the figure
      the vendor calls the Calculated Quantity, and the one `Auto fill` puts
      in. It advises rather than caps: a mobilisation advance is claimed
      against a line that shows no progress at all.
    */
    ...(calculable
      ? [{ key: 'calculatedQty', header: 'Ready to bill', width: 140, align: 'right' as const,
        render: (row: PickLine) => (
          <span className="num">
            {row.calculatedQty === undefined ? '—' : formatQuantity(row.calculatedQty)}
          </span>
        ) }]
      : []),
    { key: 'rate', header: 'Rate', width: 120, align: 'right',
      render: (row) => <span className="num">{row.rate ? formatRate(row.rate) : '—'}</span> },
    { key: 'take', header: docKey === 'stock_adjustment' && lotted ? 'Remove' : 'Take', width: 140,
      render: (row) => (
        <input
          className="control control--right"
          inputMode="decimal"
          aria-label={`Quantity to take of ${row.itemLabel}`}
          value={chosen[keyOf(row)] ?? ''}
          onChange={(e) => {
            // Read before the updater runs: React clears currentTarget once the
            // handler returns, so touching it inside setState throws on null.
            const value = e.currentTarget.value
            setChosen((was) => ({ ...was, [keyOf(row)]: value }))
            // A quantity typed is a row taken.
            setTicked((was) => {
              const next = new Set(was)
              if (value.trim() !== '' && Number(value) > 0) next.add(keyOf(row))
              else next.delete(keyOf(row))
              return next
            })
          }}
        />
      ) },
  ]

  // The ref and the T/NT flag are in the group's own heading in this view.
  const nestedColumns = columns.filter((c) => c.key !== 'boqCode' && c.key !== 'tendered')

  return (
    <div className="line-sources">
      {tabbed ? (
        <Button
          kind="ghost"
          icon={<Icon name="clip" size={18} />}
          disabled={load.isPending}
          onClick={() => load.mutate({ source: tabs[0]!.source, opening: true })}
        >
          {load.isPending ? 'Looking…' : 'Select items'}
        </Button>
      ) : null}
      {offered.filter((source) => !tabbed || TABBED[source.source] === undefined).map((source) => (
        source.source === 'excel' ? (
          <span key={source.key} style={{ display: 'inline-flex', gap: 10 }}>
            <Button
              kind="ghost"
              icon={<Icon name="up" size={18} />}
              disabled={upload.isPending}
              onClick={chooseFile}
            >
              {upload.isPending ? 'Reading…' : source.label}
            </Button>
            {/*
              Export and Import are the same file (docs/13): what comes down is
              filled in with what is already on this document, so a round trip
              through Excel is how a BOQ is edited in bulk. Elsewhere it is the
              sheet to fill in — filled in, where the document is against
              something, with what that is: the book stock to count, the BOQ
              lines to bill.
            */}
            <a className="btn btn--ghost" href={api.importTemplateUrl(docKey, documentId, section)} download>
              <Icon name="dl" size={18} />
              <span>{source.templateLabel ?? (docKey === 'project' ? 'Export BOQ' : 'Download template')}</span>
            </a>
          </span>
        ) : (
          <Button
            key={source.key}
            kind="ghost"
            icon={<Icon name="clip" size={18} />}
            disabled={load.isPending}
            onClick={() => load.mutate({ source: source.source, opening: true })}
          >
            {load.isPending ? 'Looking…' : source.label}
          </Button>
        )
      ))}

      <input
        ref={picker}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0]
          if (file) upload.mutate(file)
          e.currentTarget.value = ''
        }}
      />

      {error ? <div style={{ marginTop: 12 }}><Notice kind="bad">{error}</Notice></div> : null}

      {warnUnsaved ? (
        <Modal
          title="Unsaved BOQ changes?"
          tone="warn"
          onClose={() => setWarnUnsaved(false)}
          actions={(
            <>
              <Button kind="ghost" onClick={() => setWarnUnsaved(false)}>No, let me check it</Button>
              <Button kind="primary" onClick={() => { setWarnUnsaved(false); picker.current?.click() }}>Yes, proceed</Button>
            </>
          )}
        >
          <p>
            You may have unsaved changes in the BOQ, its estimations and overheads.
            If you proceed, the unsaved changes will be ignored.
          </p>
        </Modal>
      ) : null}

      {pick ? (
        <Modal
          title={tabbed && active && TABBED[active] ? 'Item details' : pick.title}
          wide
          onClose={() => setPick(null)}
          footerLeft={
            offeredCount === 0
              ? undefined
              : takingCount === 0
                ? 'No entries are selected'
                : `${takingCount} of ${offeredCount} selected`
          }
          actions={
            <>
              {/*
                One button that fills every line with what is ready to be
                billed. On a BOQ of four hundred lines, typing each figure by
                hand is the difference between raising a running bill in a
                minute and raising it in an afternoon — and it is where the
                quantities go wrong (docs/video-notes/06 §7).
              */}
              {calculable ? (
                <Button kind="ghost" onClick={autoFill}>Auto fill qty</Button>
              ) : (
                <Button kind="ghost" disabled={shown.length === 0} onClick={fillOpen}>
                  Fill all open quantities
                </Button>
              )}
              <Button kind="ghost" onClick={() => setPick(null)}>Cancel</Button>
              <Button kind="primary" disabled={takingCount === 0} onClick={takePicked}>
                Add what I have entered
              </Button>
            </>
          }
        >
          {tabbed && active && TABBED[active] ? (
            <nav className="tabstrip" aria-label="Where the items come from" style={{ marginBottom: 14 }}>
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={tab.source === active ? 'tabstrip__tab tabstrip__tab--active' : 'tabstrip__tab'}
                  aria-current={tab.source === active ? 'page' : undefined}
                  disabled={load.isPending}
                  onClick={() => (results[tab.source] ? setActive(tab.source) : load.mutate({ source: tab.source, opening: false }))}
                >
                  {TABBED[tab.source]}
                </button>
              ))}
            </nav>
          ) : null}
          {pick.emptyReason ? (
            <Notice kind="info">
              {tabbed && active === 'estimation' ? 'No materials estimated! ' : tabbed && active === 'request' ? 'No materials requested! ' : ''}
              {pick.emptyReason}
            </Notice>
          ) : (
            <>
              <p className="muted" style={{ marginBottom: 14 }}>
                Type a quantity against each line you need. Lines left blank are not added.
              </p>
              {/*
                A stock picker on a real site runs to hundreds of lots and a BOQ
                picker to hundreds of lines. Without a search, choosing one of
                them means scrolling for it (docs/02 §6b).
              */}
              <div className="drive-toolbar">
                <div style={{ flex: 1 }}>
                  <Input
                    value={search}
                    onChange={setSearch}
                    placeholder={lotted
                      ? 'Search by item, description, brand, document no'
                      : 'Search by material, description or brand'}
                  />
                </div>
                {estimated ? (
                  /*
                    `Item View` / `BOQ View` (docs/05 §5b, docs/16 AC-3.4). The
                    flat list is how somebody works down a list of materials;
                    the nested one is how they answer "what does this BOQ line
                    actually need", which is a different question and the one
                    the narrative text is there for.
                  */
                  <div className="segmented">
                    <button
                      type="button"
                      className={view === 'ITEM' ? 'segmented__on' : ''}
                      onClick={() => setView('ITEM')}
                    >
                      Item view
                    </button>
                    <button
                      type="button"
                      className={view === 'BOQ' ? 'segmented__on' : ''}
                      onClick={() => setView('BOQ')}
                    >
                      BOQ view
                    </button>
                  </div>
                ) : null}
                <label className="column-choice">
                  <input
                    type="checkbox"
                    checked={onlyChosen}
                    onChange={() => setOnlyChosen((on) => !on)}
                  />
                  <span>{lotted ? 'Show only selected items' : 'Show only what I have entered'}</span>
                </label>
                {estimated ? (
                  <label className="column-choice">
                    <input
                      type="checkbox"
                      checked={hideFullyRequested}
                      onChange={() => setHideFullyRequested((on) => !on)}
                    />
                    <span>Don't show fully requested</span>
                  </label>
                ) : null}
              </div>
              {view === 'BOQ' && estimated ? (
                <>
                  <div className="row row--wrap" style={{ gap: 16, alignItems: 'center', marginBottom: 8 }}>
                    <p className="helper" style={{ margin: 0 }}>
                      {matched} of {groups.length} BOQ line{groups.length === 1 ? '' : 's'} with estimates
                    </p>
                    {pick.boqOutline ? (
                      <label className="column-choice">
                        <input type="checkbox" checked={hideBoqSections} onChange={() => setHideBoqSections((on) => !on)} />
                        <span>Hide BOQ section</span>
                      </label>
                    ) : null}
                    {findInPlace && query !== '' ? (
                      <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                        <span className="num" data-testid="match-counter">
                          {matches.length === 0 ? '0 / 0' : `${Math.min(matchAt, matches.length - 1) + 1} / ${matches.length}`}
                        </span>
                        <Button kind="ghost" aria-label="Next match" disabled={matches.length === 0}
                          onClick={() => setMatchAt((i) => (i + 1) % Math.max(1, matches.length))}>▼</Button>
                        <Button kind="ghost" aria-label="Previous match" disabled={matches.length === 0}
                          onClick={() => setMatchAt((i) => (i - 1 + matches.length) % Math.max(1, matches.length))}>▲</Button>
                      </span>
                    ) : null}
                  </div>
                  {hideBoqSections ? (
                    // Only the estimate rows, in BOQ order.
                    <Table columns={nestedColumns} rows={boqOrdered} getKey={keyOf} />
                  ) : (
                    boqTree.map((node) => (
                      <div key={node.key} className="pick-group">
                        {/* A BOQ heading or narrative line: context, not something to pick. */}
                        <h3 className="subhead" style={node.lineType === 'SECTION' ? undefined : { fontWeight: 500 }}>
                          {node.ref ? <span className="mono">{highlight(node.ref)}</span> : null}{' '}
                          <span className="mono">{node.tendered === false ? 'NT' : 'T'}</span>{' '}
                          {highlight(node.narrative)}
                        </h3>
                        {node.lines.length > 0 ? <Table columns={nestedColumns} rows={node.lines} getKey={keyOf} /> : null}
                      </div>
                    ))
                  )}
                </>
              ) : (
                <Table columns={columns} rows={shown} getKey={keyOf} />
              )}
              {shown.length === 0 ? (
                <p className="helper">Nothing matches that.</p>
              ) : null}
            </>
          )}
        </Modal>
      ) : null}

      {imported ? (
        <Modal
          title="Imported from the spreadsheet"
          onClose={() => setImported(null)}
          footerLeft={imported.summary}
          actions={
            <>
              <Button kind="ghost" onClick={() => setImported(null)}>Cancel</Button>
              <Button kind="primary" disabled={imported.accepted.length === 0} onClick={takeImported}>
                Add {imported.accepted.length} line{imported.accepted.length === 1 ? '' : 's'}
              </Button>
            </>
          }
        >
          {imported.ignoredHeaders.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <Notice kind="warn">
                These columns are not on this document and were left out:{' '}
                {imported.ignoredHeaders.join(', ')}.
              </Notice>
            </div>
          ) : null}

          {imported.rejected.length > 0 ? (
            <div>
              <p style={{ marginBottom: 10 }}>
                <strong>{imported.rejected.length}</strong> row
                {imported.rejected.length === 1 ? '' : 's'} could not be read. Fix them in the
                spreadsheet and import again — the rest can be added now.
              </p>
              <ul className="import-problems">
                {imported.rejected.slice(0, 12).map((row) => (
                  <li key={row.sheetRow}>
                    <span className="num">Row {row.sheetRow}</span> — {row.problem}
                  </li>
                ))}
              </ul>
              {imported.rejected.length > 12 ? (
                <p className="muted">…and {imported.rejected.length - 12} more.</p>
              ) : null}
            </div>
          ) : (
            <p>Every row was read. Nothing was rejected.</p>
          )}
        </Modal>
      ) : null}
    </div>
  )
}

/**
 * Imported lines onto the grid.
 *
 * A line that `matchBy` says is already there — the same material in the same
 * bin on a count, the same BOQ line on a bill — takes the imported figures in
 * place, keeping its id and its number; the rest are appended. New lines are
 * numbered on from what is there, and a reference one imported line makes to
 * another by its `seq:<SECTION>:<n>` key (an estimate to its BOQ line) is
 * renumbered with it, or it would point at whatever now holds the old number.
 */
export const mergeImported = (
  lines: readonly LineRow[],
  accepted: ReadonlyArray<Record<string, unknown>>,
  matchBy: readonly string[] | undefined,
  section: string | undefined,
): LineRow[] => {
  const out: LineRow[] = [...lines]
  const sectionOf = (l: Record<string, unknown>): string => String(l['section'] ?? section ?? 'ITEM')
  const cell = (l: Record<string, unknown>, key: string): string => String(l[key] ?? '')
  const added: Array<Record<string, unknown>> = []

  for (const line of accepted) {
    // A cell the sheet left empty comes back as null, which the save refuses
    // for a text column: on the grid it is simply not there.
    const filled = Object.fromEntries(Object.entries(line).filter(([, v]) => v !== null && v !== undefined))
    const placed: Record<string, unknown> = { ...filled, ...(section && filled['section'] === undefined ? { section } : {}) }
    const at = matchBy && matchBy.length > 0
      ? out.findIndex((l) => sectionOf(l) === sectionOf(placed)
        && matchBy.every((key) => cell(l, key) === cell(placed, key)))
      : -1
    if (at >= 0) {
      const { seq: _ignored, ...figures } = placed
      out[at] = { ...out[at]!, ...figures } as LineRow
    } else {
      added.push(placed)
    }
  }

  let seq = out.reduce((top, l) => Math.max(top, Number(l['seq']) || 0), 0)
  const renumbered = new Map<string, string>()
  const fresh = added.map((line): Record<string, unknown> => {
    seq += 1
    if (line['seq'] !== undefined) renumbered.set(`seq:${sectionOf(line)}:${String(line['seq'])}`, `seq:${sectionOf(line)}:${seq}`)
    return { ...line, seq }
  })
  const relink = (value: unknown): unknown =>
    (typeof value === 'string' && renumbered.has(value) ? renumbered.get(value) : value)
  return [
    ...out,
    ...fresh.map((line) => ({
      ...line,
      ...(line['parentLineId'] !== undefined ? { parentLineId: relink(line['parentLineId']) } : {}),
      ...(line['billingTermLineId'] !== undefined ? { billingTermLineId: relink(line['billingTermLineId']) } : {}),
    }) as LineRow),
  ]
}

/** Asked for beyond what was estimated (open goes below zero only by over-asking). */
const overRequested = (line: PickLine): boolean =>
  line.availableQty !== 'RO' && line.openQty !== 'RO' && Number(line.openQty) < 0

const keyOf = (line: PickLine): string => line.fromLineId ?? line.itemId ?? line.itemLabel

/**
 * How much of this line has already been asked for.
 *
 * The server sends what the source holds and what is still open; the
 * difference is what everything raised against it so far adds up to. Computed
 * rather than sent so there is one place the two can never disagree.
 */
const requestedSoFar = (line: PickLine): string => {
  if (line.availableQty === 'RO' || line.openQty === 'RO') return ZERO_QTY
  const taken = subQty(qty(line.availableQty), qty(line.openQty))
  return Number(taken) < 0 ? ZERO_QTY : taken
}

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read'))
    reader.onload = () => {
      const result = String(reader.result)
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })
